import axios from 'axios';
import { getCSRFToken, DEFAULT_BASE_URL, clearCSRFToken } from './authAPI';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL ?? DEFAULT_BASE_URL,
  timeout: 30000,
  withCredentials: true
});

api.interceptors.request.use(async (config) => {
  try {
    const token = await getCSRFToken();
    if (token) {
      config.headers['X-CSRFToken'] = token;
      console.log('Added CSRF token to request:', config.url, token);
    } else {
      console.warn('No CSRF token available for request:', config.url);
    }

    // Ensure credentials are always sent
    config.withCredentials = true;

    return config;
  } catch (error) {
    console.error('Error in request interceptor:', error);
    return config;
  }
}, (error) => {
  return Promise.reject(error);
});

export const reportError = async (error, context = {}) => {
  try {
    // Prevent infinite loops: don't report errors from the reporting endpoint
    if (error.config && error.config.url && error.config.url.includes('/errors/')) {
      return;
    }

    const errorData = {
      type: error.name || 'JS_ERROR',
      endpoint: error.config?.url || window.location.pathname,
      status: error.response?.status || 0,
      message: error.message || 'Unknown error',
      stack_trace: error.stack || null,
    };

    // Get CSRF token to ensure request is accepted
    const token = await getCSRFToken();

    // Use fetch directly to avoid circular dependency or interceptor loops
    await fetch(`${DEFAULT_BASE_URL}/errors/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token
      },
      credentials: 'include', // Important: send session cookie
      body: JSON.stringify(errorData)
    });
  } catch (e) {
    console.error('Failed to report error:', e);
  }
};

api.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  if (error.response) {
    const status = error.response.status;

    // Handle authentication errors
    if (status === 401) {
      clearCSRFToken();
      window.location.reload();
    } else if (status === 403) {
      const errorData = error.response.data;

      // If it's a CSRF token issue, try to refresh token and retry once
      if (errorData?.detail?.includes('CSRF') || errorData?.detail?.includes('Authentication credentials')) {
        console.log('Authentication/CSRF error detected, clearing token and retrying...');
        clearCSRFToken();

        // Don't retry if this is already a retry attempt
        if (!error.config._retry) {
          error.config._retry = true;
          try {
            error.config.headers['X-CSRFToken'] = await getCSRFToken();
            return api.request(error.config);
          } catch (retryError) {
            console.error('Retry failed:', retryError);
            // If retry fails, redirect to login
            window.location.reload();
          }
        }
      }
    }
  }

  // Report other errors
  reportError(error);
  return Promise.reject(error);
});

export const fetchTickers = async (params = {}) => {
  const response = await api.get('/tickers/', { params });
  return response.data;
};

export const fetchSummary = async () => {
  const response = await api.get('/summary/');
  return response.data;
};

export const fetchCandles = async (symbol, params = {}) => {
  const response = await api.get(`/candles/${symbol}/`, { params });
  return response.data;
};

export const fetchExchanges = async () => {
  const response = await api.get('/exchanges/');
  return response.data;
};

export const fetchHealth = async () => {
  const response = await api.get('/health/');
  return response.data;
};

export const sendAlertEmail = async (emailData) => {
  const response = await api.post('/send-alert-email/', emailData);
  return response.data;
};

export const fetchAlerts = async () => {
  const response = await api.get('/alerts/');
  return response.data;
};

export const createAlert = async (alertData) => {
  const response = await api.post('/alerts/', alertData);
  return response.data;
};

export const updateAlert = async (id, alertData) => {
  const response = await api.put(`/alerts/${id}/`, alertData);
  return response.data;
};

export const deleteAlert = async (id) => {
  await api.delete(`/alerts/${id}/`);
  return true;
};

export const updateProfile = async (profileData) => {
  const response = await api.put('/profile/me/', profileData);
  return response.data;
};

export const debugAuthentication = async () => {
  console.group('🔍 Authentication Debug');

  try {
    // Check cookies
    console.log('📝 Current cookies:', document.cookie);

    // Check if we can get CSRF token
    const csrfResponse = await fetch(`${DEFAULT_BASE_URL}/auth/csrf/`, {
      credentials: 'include'
    });
    console.log('🔑 CSRF token response:', csrfResponse.status, await csrfResponse.json());

    // Check session
    const sessionResponse = await fetch(`${DEFAULT_BASE_URL}/auth/session/`, {
      credentials: 'include'
    });
    const sessionData = await sessionResponse.json();
    console.log('👤 Session data:', sessionData);

    // Test a protected endpoint
    console.log('🛡️ Testing protected endpoint...');
    const watchlistResponse = await api.get('/watchlist/');
    console.log('✅ Watchlist response:', watchlistResponse.status);

  } catch (error) {
    console.error('❌ Debug error:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    }
  }

  console.groupEnd();
};

export const checkAuthentication = async () => {
  try {
    const response = await fetch(`${DEFAULT_BASE_URL}/auth/session/`, {
      credentials: 'include'
    });
    const data = await response.json();
    return data.authenticated || false;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

export const fetchWatchlist = async () => {
  const response = await api.get('/watchlist/');
  return response.data;
};

export const addToWatchlist = async (symbol) => {
  try {
    console.log('Adding to watchlist:', symbol);
    const response = await api.post('/watchlist/add/', { symbol });
    return response.data;
  } catch (error) {
    console.error('Error adding to watchlist:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

export const removeFromWatchlist = async (symbol) => {
  try {
    console.log('Removing from watchlist:', symbol);
    const response = await api.post('/watchlist/remove/', { symbol });
    return response.data;
  } catch (error) {
    console.error('Error removing from watchlist:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

export const fetchTechnicalAnalysis = async (symbol, timeframe) => {
  const response = await api.get(`/technical-analysis/${symbol}/`, {
    params: { timeframe }
  });
  return response.data;
};

export const fetchLstmPrediction = async (symbol, lookback = 5, epochs = 1) => {
  const response = await api.get(`/predict/lstm/${symbol}/`, {
    params: { lookback, epochs },
    timeout: 300000 // 5 minutes for LSTM prediction
  });
  return response.data;
};

export const fetchSentimentAnalysis = async (symbol) => {
  const response = await api.get(`/analysis/${symbol}`);
  return response.data;
};

export default api;

